import { Container } from 'Container';
import { Image, type ImageProps } from 'Image';
import { useEffect, useState, type ReactNode } from 'react';
import { Typography } from 'Typography';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'FrontRoyalConfig';
import { useCurrentUser, useNavigationHelper, useOfflineModeManager, useTargetBrandConfig } from 'FrontRoyalAngular';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { InstitutionId } from 'Institutions';
import { getActiveInstitution } from 'Users';
import clsx from 'clsx';

import logoRedQuantic from 'vectors/logos/quantic-logo-rebrand.svg';
import logoBlueValar from 'vectors/footer/logos-valar-logo.svg';
import logoRedSmartly from 'vectors/footer/logos-smartly-logo.svg';
import logoMiyaMiya from 'images/footer/miya-miya-logo.png';
import logoMiyaMiyaRetina from 'images/footer/miya-miya-logo@2x.png';
import availableAppStore from 'vectors/available-app-store.svg';
import availableGooglePlay from 'vectors/available-google-play.svg';
import './locales/footer/footer-en.json';

const logoQuanticImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 155,
        height: 38,
        images: {
            default: logoRedQuantic,
        },
    },
};
const logoValarImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 119,
        height: 32,
        images: {
            default: logoBlueValar,
        },
    },
};
const logoSmartlyImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 137,
        height: 32,
        images: {
            default: logoRedSmartly,
        },
    },
};
const logoMiyaMiyaImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 140,
        height: 32,
        images: {
            default: logoMiyaMiya,
            retina: logoMiyaMiyaRetina,
        },
    },
};
const appStoreImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 94,
        height: 24,
        images: {
            default: availableAppStore,
        },
    },
};
const googlePlayImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 100,
        height: 24,
        images: {
            default: availableGooglePlay,
        },
    },
};

const brandLogoMap: Record<InstitutionId, Omit<ImageProps, 'alt'>> = {
    [InstitutionId.quantic]: logoQuanticImage,
    [InstitutionId.valar]: logoValarImage,
    [InstitutionId.smartly]: logoSmartlyImage,
    [InstitutionId.miya_miya]: logoMiyaMiyaImage,
};

type ActiveInstitution = ReturnType<typeof getActiveInstitution>;

/*
    getActiveInstitution returns either shape, camelCased from the vanilla user or snake_cased from the
    Iguana one, so both keys are tried.
*/
function institutionUrl(institution: ActiveInstitution, camelKey: string, snakeKey: string) {
    if (!institution) return undefined;
    const attrs = institution as unknown as Record<string, string | undefined>;
    return attrs[camelKey] ?? attrs[snakeKey];
}

// The institution's own links win over ours, so an external student gets their school's privacy policy.
function useFooterUrls() {
    const currentUser = useCurrentUser();
    const institution = getActiveInstitution(currentUser);

    return {
        ios: institutionUrl(institution, 'iosUrl', 'ios_url') || '/smartly-ios',
        android: institutionUrl(institution, 'androidUrl', 'android_url') || '/smartly-android',
        privacy: institutionUrl(institution, 'privacyPolicyUrl', 'privacy_policy_url') || '/privacy',
        terms: institutionUrl(institution, 'termsUrl', 'terms_url') || '/terms',
    };
}

/*
    Institution links can point outside the app, so use loadUrl, not a router Link. A Link turns
    "https://..." into "/https://...".
*/
function FooterLink({ url, className, children }: { url: string; className?: string; children: ReactNode }) {
    const { loadUrl } = useNavigationHelper();

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
            onClick={event => {
                event.preventDefault();
                loadUrl(url, '_blank');
            }}
        >
            {children}
        </a>
    );
}

// react2Angular mounts this outside the router, where a Link throws, so the logo uses loadRoute.
export function Footer({
    className,

    /*
        Set false where the page already reserves room at the bottom on mobile. StudentDashboard does:
        its wrapper pads for the fixed sidebar boxes, which clears the nav too, so the footer adding
        its own 55px there just leaves dead space.
    */
    reserveNavClearance = true,
}: {
    className?: string;
    reserveNavClearance?: boolean;
}) {
    const { t } = useTranslation('back_royal');
    const config = useConfig();
    const targetBrandConfig = useTargetBrandConfig();
    const showCookiePreferences = config?.gdprAppliesToUser() || false;
    const offlineModeManager = useOfflineModeManager();
    const urls = useFooterUrls();
    const { loadRoute } = useNavigationHelper();
    const AppHeaderViewModel = useAppHeaderViewModel();

    /*
        The mobile bottom nav is fixed, 55px tall and sm:hidden, so without room below it the footer's
        last line sits underneath it. The footer owns the clearance rather than each caller, because
        only the footer can see whether the nav is on the screen at all.

        The notch variant matches the nav's own `notch:mb-[env(safe-area-inset-bottom)]`.
    */
    const navClearance =
        AppHeaderViewModel.hideMobileMenu || !reserveNavClearance
            ? ''
            : 'pb-[55px] notch:pb-[calc(55px+env(safe-area-inset-bottom))] sm:pb-0';

    /* Hidden in Cordova and offline. Decided on mount only: going offline later keeps the footer. */
    const [showFooter, setShowFooter] = useState(window.CORDOVA ? false : !offlineModeManager.inOfflineMode);
    useEffect(() => {
        setShowFooter(window.CORDOVA ? false : !offlineModeManager.inOfflineMode);
    }, [offlineModeManager]);

    if (!showFooter) {
        /* Spacer: 55px matches the mobile nav, 30px matches the legacy sm spacer. A height cannot collapse. */
        return <div className="h-[55px] sm:h-7.5" />;
    }

    return (
        <footer className={clsx('bg-white', navClearance, className)}>
            <Container className="px-2.5 xs:px-[15px]">
                <div className="flex flex-col items-center gap-6 pb-6 pt-10 md:flex-row md:justify-between">
                    <a
                        href="/"
                        title={t('footer.footer.homepage') as string}
                        onClick={event => {
                            event.preventDefault();
                            loadRoute('/');
                        }}
                    >
                        <Image
                            {...brandLogoMap[targetBrandConfig.institutionId]}
                            className="h-8 flex-shrink-0"
                            alt={targetBrandConfig.brandNameShort}
                        />
                    </a>
                    <div className="flex gap-6">
                        <FooterLink url={urls.ios}>
                            <Image
                                {...appStoreImage}
                                className="flex-shrink-0 brightness-100 transition hover:brightness-75"
                                alt={t('footer.footer.available_app_store')}
                            />
                        </FooterLink>
                        <FooterLink url={urls.android}>
                            <Image
                                {...googlePlayImage}
                                className="flex-shrink-0 brightness-100 transition hover:brightness-75"
                                alt={t('footer.footer.available_google_play')}
                            />
                        </FooterLink>
                    </div>
                </div>
                <hr className="border-slate-grey-200" />
                <div className="pb-10 pt-6 text-center md:flex md:items-center md:justify-center md:pb-8">
                    <Typography variant="link" className="text-slate-grey-400">
                        {t('footer.footer.copyright')}
                        <br className="md:hidden" />
                        <span className="hidden md:inline">&nbsp;</span>
                        <FooterLink
                            url={urls.privacy}
                            className="font-bold text-slate-grey-400 transition hover:text-slate-grey hover:no-underline"
                        >
                            {t('footer.footer.privacy_policy')}
                        </FooterLink>
                        &nbsp;|&nbsp;
                        <FooterLink
                            url={urls.terms}
                            className="font-bold text-slate-grey-400 transition hover:text-slate-grey hover:no-underline"
                        >
                            {t('footer.footer.terms_and_conditions')}
                        </FooterLink>
                        {showCookiePreferences ? (
                            <>
                                &nbsp;|&nbsp;
                                <a
                                    href="/#"
                                    className="font-bold text-slate-grey-400 transition hover:text-slate-grey hover:no-underline"
                                    onClick={event => {
                                        event.preventDefault();
                                        // oxlint-disable-next-line new-cap
                                        window.Optanon?.ToggleInfoDisplay();
                                    }}
                                >
                                    {t('footer.footer.cookies')}
                                </a>
                            </>
                        ) : null}
                    </Typography>
                </div>
            </Container>
        </footer>
    );
}
